/* eslint-disable react/prop-types */

import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { TourProvider, useTour, StepType } from '@reactour/tour';

// components
import ButtonCloseTour from 'productTours/components/ButtonCloseTour';
import PageRoutes from '_routes/PageRoutes';
import SidePanelRoutes from '_routes/SidePanelRoutes';
import TourHandler from 'productTours/components/TourHandler';
import TourNavigation from 'productTours/components/TourNavigation';

// context
import { useUser } from 'shared/hooks/api/user';

// hooks
import useTourConfig from 'productTours/hooks/useTourConfig';
import useViewportHeight from 'shared/hooks/useViewportHeight';
import useWindowSize from 'shared/hooks/useWindowResize';

// styles
import * as Container from 'layout/styles/PageLayout.style';
import colours from 'theme/atoms/colours';

export default function PageContainer(): React.ReactElement {
  const location = useLocation();
  const history = useHistory();
  const { data: user } = useUser();
  const windowSize = useWindowSize();
  const windowWidth = windowSize[0];

  const tourSteps = useTourConfig(history);

  useViewportHeight();

  const inECoach = location.pathname.toLowerCase().includes('ecoach');

  return (
    <TourProvider
      key={user?.language}
      steps={tourSteps as StepType[]}
      disableInteraction={false} // Allow interaction with highlighted elements
      components={{
        Close: ({ onClick }) => {
          const { currentStep } = useTour();
          return (
            <ButtonCloseTour
              onClick={onClick}
              currentStep={currentStep}
              numSteps={tourSteps.length}
              tourType={user.is_demo ? 'demo' : 'onboarding'}
            />
          );
        },
        Navigation: TourNavigation,
      }}
      onClickMask={() => {}} // prevent tour from closing on background click
      styles={{
        popover: (base) => ({
          ...base,
          '--reactour-accent': colours.primary[500],
          borderRadius: '20px',
          minWidth: `${windowWidth > 768 ? '350px' : '250px'}`,
          paddingTop: '40px',
        }),
        maskArea: (base) => ({ ...base, rx: 20 }),
        maskWrapper: (base) => ({ ...base, opacity: '0.5' }),
        badge: (base) => ({ ...base, display: 'none' }), // tour step number
        controls: (base) => ({ ...base, display: 'none' }), // Hide default controls as we use our custom Navigation
        close: (base) => ({ ...base, left: 'auto', right: 20, top: 20 }),
      }}
    >
      <TourHandler user={user} />
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Container.ScreenContainer id="screen" inDemo={user.is_demo}>
          <SidePanelRoutes />
          <Container.PageContainer inECoach={inECoach}>
            <PageRoutes />
          </Container.PageContainer>
        </Container.ScreenContainer>
      </div>
    </TourProvider>
  );
}
